import { useEffect } from "react";

// =============================================================================
// GoHighLevel chat-widget loader.
//
// Renders nothing visible itself. On mount, injects the GHL loader.js script
// at the bottom of <body> (matching GHL's "embed in footer" instruction).
// The loader reads the two data-* attributes to identify which widget to
// render — it then mounts its own floating launcher button at the
// bottom-right corner of the viewport.
//
// We mount this once from <Layout>, so it appears on every page automatically.
// A small marker attribute guards against double-injection if React HMR or
// React Router transitions remount Layout.
//
// SHADOW DOM POSITION OVERRIDE
// -----------------------------
// The launcher button (`button.lc_text-widget--bubble`) and the greeting
// bubble (`div.lc_text-widget`) live INSIDE the chat-widget's open shadow
// root with their own `position: fixed; bottom: 20px; right: 20px` styles.
// External stylesheets cannot pierce Shadow DOM in CSS, so we have to
// inject a <style> element directly into the shadow root once the widget
// has rendered. The poll below waits for the element + shadow root, then
// appends the override style.
// =============================================================================

const SCRIPT_SRC = "https://widgets.leadconnectorhq.com/loader.js";
const RESOURCES_URL = "https://widgets.leadconnectorhq.com/chat-widget/loader.js";
const WIDGET_ID = "6a3ab97b5d45ca4cd221b20d";

// Marker so we never inject the script twice.
const MARKER = "data-abc-ghl-chat";
const SHADOW_STYLE_MARKER = "data-abc-chat-shadow";

// CSS appended INTO the chat-widget's shadow root. The selectors target
// the GHL launcher button + greeting prompt by their internal class names.
// `@media (max-width: 1279px)` matches Tailwind's `xl` breakpoint — the
// same breakpoint at which our `StickyMobileCTA` is visible
// (`xl:hidden`). At ≥1280px, the call bar is hidden and the widget can sit
// at its default bottom-right position.
const SHADOW_CSS = `
  @media (max-width: 1279px) {
    .lc_text-widget,
    .lc_text-widget--bubble,
    #lc_text-widget,
    #lc_text-widget--btn {
      bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important;
    }
  }
`;

function injectShadowStyles(host) {
  if (!host || !host.shadowRoot) return false;
  if (host.shadowRoot.querySelector(`style[${SHADOW_STYLE_MARKER}]`)) return true;
  const style = document.createElement("style");
  style.setAttribute(SHADOW_STYLE_MARKER, "");
  style.textContent = SHADOW_CSS;
  host.shadowRoot.appendChild(style);
  return true;
}

export default function ChatWidget() {
  useEffect(() => {
    // Inject the loader script once. The MARKER check makes this safely
    // idempotent across React StrictMode's double-mount and across page-
    // level navigations (script element persists in <body>).
    if (!document.querySelector(`script[${MARKER}]`)) {
      const s = document.createElement("script");
      s.setAttribute("data-resources-url", RESOURCES_URL);
      s.setAttribute("data-widget-id", WIDGET_ID);
      s.setAttribute(MARKER, "");
      s.src = SCRIPT_SRC;
      document.body.appendChild(s);
    }

    // Always start polling on mount — the StrictMode double-effect will
    // clear the first interval (cleanup) and start a fresh one (re-mount),
    // and `injectShadowStyles` is itself idempotent so the worst case is a
    // redundant poll tick. We try every 200ms for up to 20s, which covers
    // slow networks without leaking forever.
    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      const host = document.querySelector("chat-widget");
      if (injectShadowStyles(host) || attempts > 100) {
        window.clearInterval(intervalId);
      }
    }, 200);

    return () => window.clearInterval(intervalId);
  }, []);

  return null;
}
